import { memo, useEffect, useState } from 'react';
import css from './ConfirmAppointmentInformation.module.scss';
import { GoMotoComponent } from '../../../API/GoMoto.types';
import { Events } from '../../../API/Enums';
import { IAppointment, insertAuditTrail } from '../../../API/GoMoto';
import { useAppointmentState, useAppointmentUpdateAndPersist, useMaskPhone, useRecordInteraction } from '../../../context/Appointment/appointment';
import { useKioskTimeTable } from '../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../context/Localization/localization';
import { Locales, LocalizationType } from '../../../context/Localization/localization.types';
import { GMAppointmentName } from '../../../controls/AppointmentName';
import { CTAButton, GMGenericButton } from '../../../controls/ButtonBase';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { GMCapitalize } from '../../../controls/Case';
import { GMDialogError } from '../../../controls/Dialog';
import { Heading } from '../../../controls/Heading/Heading';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { useFormatAppointmentDate, getFullDisplayName } from '../../../helpers/Appointment';
import { useDisplayLocalizedVehicle } from '../../../helpers/displayVehicle';
import { formatAsPhoneNumber } from '../../../helpers/Number';
import { ScrollableBody } from '../../Layout/ScrollableBody/ScrollableBody';
import { ConfirmAppointmentInformationProps } from './ConfirmAppointmentInformation.types';

/**
 * Displays the customer's information after pulling the customer's information up at the kiosk via
 * a QR Code so that the customer can confirm their information before continuing to finish the check-in
 * appointment.
 *
 * @param onExistingCustomer - {@link ConfirmAppointmentInformationProps.onExistingCustomer onExistingCustomer} description.
 * @param onInformationCorrect - {@link ConfirmAppointmentInformationProps.onInformationCorrect onInformationCorrect} description.
 * @param onInformationIncorrect - {@link ConfirmAppointmentInformationProps.onInformationIncorrect onInformationIncorrect} description.
 */
export const ConfirmAppointmentInformation: GoMotoComponent<ConfirmAppointmentInformationProps> = memo(({
    onExistingCustomer,
    onInformationCorrect,
    onInformationIncorrect,
}) => {
    const appointment: IAppointment = useAppointmentState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const { computeNextAvailableCheckinTime, getIsAppointmentTimeAfterOffset } = useKioskTimeTable();
    const formatAppointmentDate = useFormatAppointmentDate();
    const maskPhone: boolean = useMaskPhone();
    const Localization: LocalizationType = useLocalization();
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();
    const recordInteraction: VoidFunction = useRecordInteraction(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [confirmed, setConfirmed] = useState<boolean>(false);

    error && console.error(error);

    useEffect(() => {
        // This is returned by the web service to indicate that the customer exists already (Currently DT only)
        if (appointment.dmsCustomerKey === '-1') {
            onExistingCustomer();
        }
        else if (confirmed) {
            onInformationCorrect();
        }
    });

    if (loading) {
        return (
            <Spinner />
        );
    }
    else {
        return (
            <ScrollableBody
                heading={
                    <Heading
                        primaryHeading={<GMAppointmentName />}
                        secondaryHeading={Localization.ConfirmYourAppointment}
                        leftAlign
                    />
                }
                proceedButtons={
                    <GMButtonGroup
                        alignment={'center'}
                        buttons={[
                            (
                                <CTAButton
                                    styleType={'primary'}
                                    onClick={async () => {
                                        setLoading(true);
                                        await setAppointment(
                                            {
                                                ...appointment,
                                                // TODO: This doesn't calculate right at all
                                                appointmentDateTime: getIsAppointmentTimeAfterOffset(appointment.appointmentDateTime)
                                                    ? computeNextAvailableCheckinTime()
                                                        .toISOString()
                                                    : appointment.appointmentDateTime,
                                            },
                                            true,
                                            true,
                                        )
                                            .catch(setError);
                                        try {
                                            await insertAuditTrail(appointment.key, Events.Mobile_Check_In_At_Kiosk_Started);
                                        }
                                        catch (auditError) {
                                            console.error('Failed to log Mobile Check-In At Kiosk Started audit event', auditError);
                                        }
                                        setLoading(false);
                                        setConfirmed(true);
                                    }}
                                    title={Localization.ClickToConfirmCorrect}
                                    label={Localization.YesItIs}
                                    kioskOptions={{ square: true }}
                                    id={Localization.ClickToConfirmCorrect}
                                    ariaLabel={Localization.ClickToConfirmCorrect}
                                />
                            ),
                            (
                                <CTAButton
                                    styleType={'secondary'}
                                    onClick={() => {
                                        onInformationIncorrect();
                                        recordInteraction();
                                    }}
                                    title={Localization.ClickToReturnToEntry}
                                    label={Localization.NoItIsNot}
                                    kioskOptions={{
                                        square: true,
                                        squareOptions: {
                                            roundedIcon: (
                                                <GMRoundedIcon
                                                    themeType={'primary'}
                                                    allowInvertedPrimary
                                                    roundedBackgroundSize={'large'}
                                                >
                                                    <GMSizedIcon iconSize={'custom'} iconType={'No'} />
                                                </GMRoundedIcon>
                                            ),
                                        },
                                    }}
                                    id={Localization.ClickToReturnToEntry}
                                    ariaLabel={Localization.ClickToReturnToEntry}
                                />
                            ),
                        ]}
                    />
                }
                unsetButtons
                topDivider
            >
                <div className={css.appointmentWrapper}>
                    <GMGenericButton id={Localization.ClickToOpenAppointment} ariaLabel={Localization.ClickToOpenAppointment}>
                        <div className={css.appointmentCard}>
                            <div className={css.information}>
                                <div className={css.icon}>
                                    <GMRoundedIcon themeType={'secondary'} roundedBackgroundSize={'large'}>
                                        <GMSizedIcon
                                            iconSize={'large'}
                                            iconType={'Calendar'}
                                            ariaLabel={Localization.CalendarIcon}
                                        />
                                    </GMRoundedIcon>
                                </div>
                                <div className={css.content}>
                                    <span className={css.name}>{getFullDisplayName(appointment)}</span>
                                    <div>{formatAppointmentDate(appointment, {
                                        [Locales.en]: 'MMMM Do | hh:mm A',
                                        [Locales.es]: 'MMMM D | hh:mm A',
                                    })}
                                    </div>
                                    <GMCapitalize>
                                        {displayLocalizedVehicle(appointment)}
                                    </GMCapitalize>
                                    {appointment.phone?.length > 0 && <div>{formatAsPhoneNumber(appointment.phone, maskPhone)}</div>}
                                </div>
                            </div>
                        </div>
                    </GMGenericButton>
                </div>
                <GMDialogError
                    ariaLabel={Localization.AnErrorHasOccured}
                    open={!!error}
                    title={Localization.AnErrorHasOccured}
                    onClose={() => setError(undefined)}
                >
                    <div style={{ display: 'none' }}>{error}</div>
                </GMDialogError>
            </ScrollableBody>
        );
    }
});
